import { useState, useEffect, useCallback } from 'react';
import { Drawer } from 'vaul';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Bell, CreditCard, Moon, Shield, X, Check } from 'lucide-react';
import { Button, Input, sectionLabelVariants } from '@perch/ui';
import { useIsMobile } from '../../hooks/useIsMobile';
import { supabase, SUPABASE_FUNCTIONS_URL } from '../../lib/supabase';
import { ThemeToggleCompact } from '../ui/ThemeToggle';
import { isOptedOut, optOut, optIn } from '../../lib/consent';
import { enableAnalytics, disableAnalytics } from '../../lib/posthog';
import { BillingSection } from './BillingSection';
import type { User as SupabaseUser, Session } from '@supabase/supabase-js';
import type { UserProfile, BookingStats } from '../../lib/types';

// --- Types ---

type SettingsTab = 'profile' | 'notifications' | 'billing' | 'preferences' | 'account';

const tabs: { id: SettingsTab; label: string; icon: React.ReactNode }[] = [
  {
    id: 'profile',
    label: 'Profile',
    icon: <User className="w-4 h-4" strokeWidth={1.5} />,
  },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: <Bell className="w-4 h-4" strokeWidth={1.5} />,
  },
  {
    id: 'billing',
    label: 'Billing',
    icon: <CreditCard className="w-4 h-4" strokeWidth={1.5} />,
  },
  {
    id: 'preferences',
    label: 'Preferences',
    icon: <Moon className="w-4 h-4" strokeWidth={1.5} />,
  },
  {
    id: 'account',
    label: 'Account',
    icon: <Shield className="w-4 h-4" strokeWidth={1.5} />,
  },
];

// --- Props ---

export interface SettingsPanelProps {
  isOpen: boolean;
  activeTab: SettingsTab;
  onTabChange: (tab: SettingsTab) => void;
  onClose: () => void;
  user: SupabaseUser;
  session: Session | null;
  profile: UserProfile | null;
  stats: BookingStats;
  lifetimeSavings: number;
  signOut: () => void;
  loadProfile: () => void;
}

export type { SettingsTab };

// --- Main Component ---

export function SettingsPanel({
  isOpen,
  activeTab,
  onTabChange,
  onClose,
  user,
  session,
  profile,
  stats,
  lifetimeSavings,
  signOut,
  loadProfile,
}: SettingsPanelProps) {
  const isMobile = useIsMobile();
  const userId = user.id;
  const userEmail = user.email || '';
  const sessionToken = session?.access_token || '';

  // --- Profile state ---
  const [firstName, setFirstName] = useState(profile?.first_name || '');
  const [lastName, setLastName] = useState(profile?.last_name || '');
  const [saving, setSaving] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState(false);

  // --- Email notification state ---
  const [emailNotifEnabled, setEmailNotifEnabled] = useState(profile?.email_notifications_enabled !== false);
  const [emailNotifSaving, setEmailNotifSaving] = useState(false);
  const [emailNotifError, setEmailNotifError] = useState<string | null>(null);

  // --- Account state ---
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deletionLoading, setDeletionLoading] = useState(false);
  const [deletionError, setDeletionError] = useState<string | null>(null);

  // Sync form state when profile loads/changes
  useEffect(() => {
    setFirstName(profile?.first_name || '');
    setLastName(profile?.last_name || '');
    setEmailNotifEnabled(profile?.email_notifications_enabled !== false);
  }, [profile]);

  // Close on Escape (desktop only — Vaul handles its own)
  useEffect(() => {
    if (!isOpen || isMobile) return;
    function handleEscape(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isMobile, onClose]);

  // Lock body scroll on desktop
  useEffect(() => {
    if (!isMobile && isOpen) {
      document.body.style.overflow = 'hidden';
    } else if (!isMobile) {
      document.body.style.overflow = '';
    }
    return () => {
      if (!isMobile) document.body.style.overflow = '';
    };
  }, [isOpen, isMobile]);

  // --- Handlers ---

  const handleSaveProfile = useCallback(async () => {
    const MAX_NAME_LENGTH = 50;
    const trimmedFirst = firstName.trim();
    const trimmedLast = lastName.trim();
    if (!trimmedFirst || !trimmedLast) {
      setProfileError('Please enter both first and last name');
      return;
    }
    if (trimmedFirst.length > MAX_NAME_LENGTH || trimmedLast.length > MAX_NAME_LENGTH) {
      setProfileError(`Names must be ${MAX_NAME_LENGTH} characters or less`);
      return;
    }
    try {
      setSaving(true);
      setProfileError(null);
      const { error } = await supabase.from('user_profiles').upsert({
        id: userId,
        first_name: trimmedFirst,
        last_name: trimmedLast,
        updated_at: new Date().toISOString(),
      });
      if (error) throw error;
      setProfileSuccess(true);
      loadProfile();
      setTimeout(() => setProfileSuccess(false), 2000);
    } catch (err) {
      setProfileError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [firstName, lastName, userId, loadProfile]);

  const handleToggleEmailNotifications = useCallback(async () => {
    const newValue = !emailNotifEnabled;
    setEmailNotifEnabled(newValue);
    setEmailNotifSaving(true);
    setEmailNotifError(null);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ email_notifications_enabled: newValue, updated_at: new Date().toISOString() })
        .eq('id', userId);
      if (error) throw error;
      loadProfile();
    } catch {
      setEmailNotifEnabled(!newValue);
      setEmailNotifError('Failed to update notification preference');
    } finally {
      setEmailNotifSaving(false);
    }
  }, [emailNotifEnabled, userId, loadProfile]);

  const handleExportData = useCallback(async () => {
    try {
      setExportLoading(true);
      setExportError(null);
      const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/export-data`, {
        headers: { 'Authorization': `Bearer ${sessionToken}` },
      });
      if (!response.ok) throw new Error('Export failed');
      const data = await response.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `perch-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      setExportError('Failed to export data. Please try again.');
    } finally {
      setExportLoading(false);
    }
  }, [sessionToken]);

  const handleDeleteAccount = useCallback(async () => {
    if (deleteConfirmText !== 'DELETE') return;
    try {
      setDeletionLoading(true);
      setDeletionError(null);
      const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/delete-account`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${sessionToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirmation: 'DELETE' }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to delete account');
      loadProfile();
      setShowDeleteConfirm(false);
      setDeleteConfirmText('');
      signOut();
    } catch (err) {
      setDeletionError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setDeletionLoading(false);
    }
  }, [deleteConfirmText, sessionToken, loadProfile, signOut]);

  const handleCancelDeletion = useCallback(async () => {
    try {
      setDeletionLoading(true);
      const response = await fetch(`${SUPABASE_FUNCTIONS_URL}/cancel-deletion`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${sessionToken}`, 'Content-Type': 'application/json' },
      });
      if (!response.ok) throw new Error('Failed to cancel deletion');
      loadProfile();
    } catch (err) {
      console.error('Cancel deletion error:', err);
    } finally {
      setDeletionLoading(false);
    }
  }, [sessionToken, loadProfile]);

  // --- Panel content (shared between desktop and mobile) ---

  const panelContent = (
    <div className="flex flex-col h-full">
      {/* Identity banner */}
      <div className="flex items-center gap-3 px-6 pb-4">
        <div className="w-10 h-10 bg-stone-900 dark:bg-stone-100 rounded-full flex items-center justify-center text-base font-medium text-stone-100 dark:text-stone-900 flex-shrink-0">
          {(profile?.first_name || userEmail)?.charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          {profile?.first_name && (
            <p className="text-sm font-medium text-stone-900 dark:text-stone-100 truncate">
              {profile.first_name} {profile.last_name}
            </p>
          )}
          <p className="text-xs text-stone-400 dark:text-stone-500 truncate">{userEmail}</p>
          <p className="text-xs text-stone-400 dark:text-stone-500">
            {profile?.created_at && (
              <>Member since {new Date(profile.created_at).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</>
            )}
            {stats.total_bookings > 0 && <> · {stats.total_bookings} {stats.total_bookings === 1 ? 'trip' : 'trips'}</>}
            {lifetimeSavings > 0 && <> · ${Math.round(lifetimeSavings).toLocaleString()} saved</>}
          </p>
        </div>
      </div>

      {/* Tab row */}
      <div className="px-6 pb-4 border-b border-stone-200/60 dark:border-stone-700/50">
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => onTabChange(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900'
                  : 'text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 hover:text-stone-700 dark:hover:text-stone-300'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Active section — fixed height so panel never resizes between tabs */}
      <div className="overflow-y-auto px-6 py-5 h-[400px]">
        {activeTab === 'profile' && (
          <ProfileSection
            firstName={firstName}
            lastName={lastName}
            setFirstName={setFirstName}
            setLastName={setLastName}
            profileError={profileError}
            profileSuccess={profileSuccess}
            saving={saving}
            onSave={handleSaveProfile}
          />
        )}
        {activeTab === 'notifications' && (
          <NotificationsSection
            emailNotifEnabled={emailNotifEnabled}
            emailNotifSaving={emailNotifSaving}
            emailNotifError={emailNotifError}
            onToggle={handleToggleEmailNotifications}
          />
        )}
        {activeTab === 'billing' && (
          <BillingSection sessionToken={sessionToken} profile={profile} onProfileRefresh={loadProfile} />
        )}
        {activeTab === 'preferences' && (
          <PreferencesSection />
        )}
        {activeTab === 'account' && (
          <AccountSection
            userEmail={userEmail}
            profile={profile}
            exportLoading={exportLoading}
            exportError={exportError}
            showDeleteConfirm={showDeleteConfirm}
            deleteConfirmText={deleteConfirmText}
            deletionLoading={deletionLoading}
            deletionError={deletionError}
            onExport={handleExportData}
            onDeleteAccount={handleDeleteAccount}
            onCancelDeletion={handleCancelDeletion}
            setShowDeleteConfirm={setShowDeleteConfirm}
            setDeleteConfirmText={setDeleteConfirmText}
            setDeletionError={setDeletionError}
          />
        )}
      </div>
    </div>
  );

  // --- Mobile: Vaul bottom sheet ---

  if (isMobile) {
    return (
      <Drawer.Root open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 z-50 bg-black/60 dark:bg-black/80" />
          <Drawer.Content className="fixed inset-x-0 bottom-0 z-50 flex flex-col max-h-[92vh] rounded-t-2xl outline-none">
            <div className="absolute inset-0 bg-gradient-to-br from-stone-50 via-white to-stone-50 dark:from-stone-900 dark:via-stone-900 dark:to-stone-800 rounded-t-2xl" />
            <div className="relative border border-b-0 border-stone-200/60 dark:border-stone-700/50 rounded-t-2xl shadow-2xl shadow-stone-900/10 dark:shadow-black/30 flex flex-col overflow-hidden flex-1">
              {/* Drag handle */}
              <div className="flex justify-center pt-3 pb-1">
                <Drawer.Handle className="w-10 h-1.5 rounded-full bg-stone-300 dark:bg-stone-600" />
              </div>

              {/* Header */}
              <div className="flex items-center justify-between px-6 pt-2 pb-3">
                <Drawer.Title className="text-lg font-medium text-stone-900 dark:text-stone-100">Settings</Drawer.Title>
                <button
                  onClick={onClose}
                  className="w-10 h-10 flex items-center justify-center rounded-full text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
                  aria-label="Close settings"
                >
                  <X className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto pb-[max(1.5rem,env(safe-area-inset-bottom))]">
                {panelContent}
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    );
  }

  // --- Desktop: centered overlay dialog with AnimatePresence ---

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 dark:bg-black/80 p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
          <motion.div
            className="relative w-full max-w-2xl max-h-[85vh] overflow-hidden"
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          >
            {/* Gradient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-stone-50 via-white to-stone-50 dark:from-stone-900 dark:via-stone-900 dark:to-stone-800 rounded-2xl" />

            <div className="relative border border-stone-200/60 dark:border-stone-700/50 rounded-2xl shadow-2xl shadow-stone-900/10 dark:shadow-black/30 flex flex-col max-h-[85vh]">
              {/* Header */}
              <div className="flex items-center justify-between px-6 pt-6 pb-3 flex-shrink-0">
                <h2 className="text-lg font-medium text-stone-900 dark:text-stone-100">Settings</h2>
                <button
                  onClick={onClose}
                  className="w-10 h-10 flex items-center justify-center rounded-full text-stone-400 dark:text-stone-500 hover:text-stone-600 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 transition-colors"
                  aria-label="Close settings"
                >
                  <X className="w-5 h-5" strokeWidth={1.5} />
                </button>
              </div>

              {/* Panel content */}
              {panelContent}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// --- Section Components ---

function ToggleSwitch({
  enabled,
  disabled,
  onToggle,
}: {
  enabled: boolean;
  disabled?: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      disabled={disabled}
      onClick={onToggle}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out disabled:opacity-50 ${
        enabled ? 'bg-stone-900 dark:bg-stone-100' : 'bg-stone-300 dark:bg-stone-700'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white dark:bg-stone-900 shadow ring-0 transition duration-200 ease-in-out ${
          enabled ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

function ProfileSection({
  firstName,
  lastName,
  setFirstName,
  setLastName,
  profileError,
  profileSuccess,
  saving,
  onSave,
}: {
  firstName: string;
  lastName: string;
  setFirstName: (v: string) => void;
  setLastName: (v: string) => void;
  profileError: string | null;
  profileSuccess: boolean;
  saving: boolean;
  onSave: () => void;
}) {
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <Input
          label="First Name"
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          placeholder="John"
        />
        <Input
          label="Last Name"
          type="text"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          placeholder="Smith"
        />
      </div>
      {profileError && (
        <p className="text-sm text-red-600 dark:text-red-400 mb-3">{profileError}</p>
      )}
      {profileSuccess && (
        <p className="text-sm text-emerald-600 dark:text-emerald-400 mb-3 flex items-center gap-1.5">
          <Check className="w-4 h-4" />
          Saved
        </p>
      )}
      <Button onClick={onSave} disabled={saving} size="sm" className="px-4 py-2">
        {saving ? 'Saving...' : 'Save'}
      </Button>
    </div>
  );
}

function NotificationsSection({
  emailNotifEnabled,
  emailNotifSaving,
  emailNotifError,
  onToggle,
}: {
  emailNotifEnabled: boolean;
  emailNotifSaving: boolean;
  emailNotifError: string | null;
  onToggle: () => void;
}) {
  return (
    <div>
      <label className={sectionLabelVariants({ variant: 'default' }) + ' block mb-3'}>
        Email
      </label>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-stone-900 dark:text-stone-100">Email Notifications</p>
          <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">
            Receive emails when your flights are rebooked at a lower price
          </p>
          {emailNotifError && (
            <p className="text-xs text-red-600 dark:text-red-400 mt-1">{emailNotifError}</p>
          )}
        </div>
        <ToggleSwitch enabled={emailNotifEnabled} disabled={emailNotifSaving} onToggle={onToggle} />
      </div>
    </div>
  );
}

function PreferencesSection() {
  const [analyticsEnabled, setAnalyticsEnabled] = useState(() => !isOptedOut());

  const toggleAnalytics = () => {
    if (analyticsEnabled) {
      optOut();
      disableAnalytics();
      setAnalyticsEnabled(false);
    } else {
      optIn();
      enableAnalytics();
      setAnalyticsEnabled(true);
    }
  };

  return (
    <div>
      <div>
        <label className={sectionLabelVariants({ variant: 'default' }) + ' block mb-3'}>
          Theme
        </label>
        <ThemeToggleCompact />
      </div>

      <div className="mt-8">
        <label className={sectionLabelVariants({ variant: 'default' }) + ' block mb-3'}>
          Analytics
        </label>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-stone-900 dark:text-stone-100">Usage Analytics</p>
            <p className="text-xs text-stone-500 dark:text-stone-400 mt-0.5">Help improve Perch with anonymous usage data</p>
          </div>
          <ToggleSwitch enabled={analyticsEnabled} onToggle={toggleAnalytics} />
        </div>
      </div>
    </div>
  );
}

function AccountSection({
  userEmail,
  profile,
  exportLoading,
  exportError,
  showDeleteConfirm,
  deleteConfirmText,
  deletionLoading,
  deletionError,
  onExport,
  onDeleteAccount,
  onCancelDeletion,
  setShowDeleteConfirm,
  setDeleteConfirmText,
  setDeletionError,
}: {
  userEmail: string;
  profile: UserProfile | null;
  exportLoading: boolean;
  exportError: string | null;
  showDeleteConfirm: boolean;
  deleteConfirmText: string;
  deletionLoading: boolean;
  deletionError: string | null;
  onExport: () => void;
  onDeleteAccount: () => void;
  onCancelDeletion: () => void;
  setShowDeleteConfirm: (v: boolean) => void;
  setDeleteConfirmText: (v: string) => void;
  setDeletionError: (v: string | null) => void;
}) {
  return (
    <div>
      <p className="text-sm text-stone-500 dark:text-stone-400 mb-6">
        {userEmail}
      </p>

      {/* Your Data */}
      <div className="mb-8">
        <label className={sectionLabelVariants({ variant: 'default' }) + ' block mb-3'}>
          Your Data
        </label>
        <p className="text-sm text-stone-500 dark:text-stone-400 mb-3">
          Download all your Perch data as a JSON file.
        </p>
        <button
          onClick={onExport}
          disabled={exportLoading}
          className="px-4 py-2 bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-sm font-medium rounded-full hover:opacity-90 disabled:opacity-50"
        >
          {exportLoading ? 'Exporting...' : 'Download My Data'}
        </button>
        {exportError && (
          <p className="text-sm text-red-600 dark:text-red-400 mt-2">{exportError}</p>
        )}
      </div>

      {/* Danger Zone */}
      <div>
        <label className="block text-xs font-medium text-red-500 dark:text-red-400 uppercase tracking-widest mb-3">
          Danger Zone
        </label>

        {profile?.deletion_requested_at ? (
          <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800/50 rounded-xl">
            <p className="text-sm text-red-800 dark:text-red-200 mb-3">
              Your account is scheduled for deletion on{' '}
              <span className="font-medium">
                {new Date(new Date(profile.deletion_requested_at).getTime() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>.
            </p>
            <button
              onClick={onCancelDeletion}
              disabled={deletionLoading}
              className="px-4 py-2 bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-sm font-medium rounded-full hover:opacity-90 disabled:opacity-50"
            >
              {deletionLoading ? 'Cancelling...' : 'Cancel Deletion'}
            </button>
          </div>
        ) : showDeleteConfirm ? (
          <div className="p-4 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800/50 rounded-xl space-y-3">
            <p className="text-sm text-red-800 dark:text-red-200">
              This will permanently delete your account, bookings, and price history after a 30-day grace period. Type <span className="font-mono font-medium">DELETE</span> to confirm.
            </p>
            <Input
              type="text"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE"
              className="border-red-200 dark:border-red-800 focus:border-red-500 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.1)]"
            />
            {deletionError && (
              <p className="text-sm text-red-600 dark:text-red-400">{deletionError}</p>
            )}
            <div className="flex gap-2">
              <button
                onClick={onDeleteAccount}
                disabled={deletionLoading || deleteConfirmText !== 'DELETE'}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-full hover:bg-red-700 disabled:opacity-50"
              >
                {deletionLoading ? 'Deleting...' : 'Delete My Account'}
              </button>
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); setDeletionError(null); }}
                className="px-4 py-2 text-stone-600 dark:text-stone-400 text-sm font-medium hover:text-stone-900 dark:hover:text-stone-100 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-sm text-stone-500 dark:text-stone-400 mb-3">
              Permanently delete your account and all associated data.
            </p>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-full hover:bg-red-700"
            >
              Delete My Account
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
